import { change, getFormValues, reset } from "redux-form";
import { find, keys, map, omit, pick, reduce } from "lodash";
import { push } from "@lagunovsky/redux-react-router";
import { split } from "lodash";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { BULK_ITEM_CATEGORY_OTHER_ID } from "src/constants";
import {
  BulkyItemSchedulerPayload,
  BulkyItemsCategory,
  ContactInformation,
  SchedulerWizardFormValues,
  TechnicalType,
} from "src/interfaces";
import { Column, Container, Row, Separator, TextDisplay, Wrapper } from "src/styled";
import { createErrorNotification, createSuccessNotification, translate } from "src/services";
import { ProgressBar, RightDisplayPanel } from "src/components";
import { saveScheduledItem, updateContactInfo } from "src/redux/reducers";
import { useAppDispatch, useAppSelector } from "src/redux/store";
import AddressForm, { WIZARD_FORM_NAME } from "./WizardForm/AddressForm";
import BulkyBGImage from "src/assets/images/bulky_bg_1.jpg";
import ConfirmationScreen from "./WizardForm/summarySections/ConfirmationScreen";
import ContactInfoForm from "./WizardForm/ContactInfoForm";
import ItemsPickupDateForm from "./WizardForm/ItemsPickupDateForm";
import ItemsSelectionForm from "./WizardForm/ItemsSelectionForm";
import RubiconLogo from "src/assets/svg/RubiconLogoPrimary.svg";
import SummarySection from "./WizardForm/summarySections/SummarySection";
import WelcomeBackScreen from "./WizardForm/WelcomeBackScreen";

export enum WizardSteps {
  address = 1,
  itemsSelection = 2,
  itemsDates = 3,
  contactInfo = 4,
  confirmation = 5,
  welcomeBack = 6,
}

interface Props {
  isSummary?: boolean;
  isFromConfirmation?: boolean;
}

const SchedulerWizardPage = ({ isSummary = false, isFromConfirmation }: Props) => {
  const [step, setStep] = useState(
    isFromConfirmation
      ? WizardSteps.confirmation
      : isSummary
      ? WizardSteps.welcomeBack
      : WizardSteps.address
  );

  const { pathname } = useLocation();
  const vendorFromUrl = split(pathname, "/")[1];

  const [hasCompletedOneStep, setHasCompletedOneStep] = useState(isSummary);

  const vendorUrl = useAppSelector((state) => state.auth.vendorURL);

  const scheduledItem = useAppSelector((state) => state.bulkyItemsScheduler.scheduledPickupData);

  const confirmationId = useAppSelector((state) => state.bulkyItemsScheduler.confirmationId);

  const { addressValidation: addressValidationError, maxItemsPerRequestReached } = useAppSelector(
    (state) => state.bulkyItemsScheduler.errors
  );

  const categoryTypes = useAppSelector((state) => state.categoryTypes.categoryTypes);

  const bulkyItemsTypes = useAppSelector((state) => state.bulkyItemsTypes.bulkyItemsTypes);

  const citySettings = useAppSelector((state) => state.citySettings.settings?.categories);

  const addressValidation = useAppSelector((state) => state.bulkyItemsScheduler.addressValidation);

  const thisFormValues = useAppSelector(
    getFormValues(WIZARD_FORM_NAME)
  ) as SchedulerWizardFormValues;

  const dispatch = useAppDispatch();

  // fill form with data from scheduled item if it exists
  useEffect(() => {
    if (isSummary && scheduledItem) {
      dispatch(
        change(WIZARD_FORM_NAME, "address", {
          ...scheduledItem.address,
          formattedAddress: scheduledItem.address.line1,
        })
      );
      scheduledItem.email && dispatch(change(WIZARD_FORM_NAME, "email", scheduledItem.email));

      const selectedItems = reduce(
        map(scheduledItem.items, (item) => {
          const settingCategory = find(citySettings, {
            bulkyCategoryTypeId: item.bulkyCategoryTypeId,
          }) as BulkyItemsCategory;

          const settingItem =
            find(settingCategory?.items, {
              bulkyItemTypeId: item.bulkyItemTypeId,
            }) || find(settingCategory?.items, { id: item.id });

          const categoryType = find(categoryTypes, {
            id: item.bulkyCategoryTypeId,
          });

          const itemType = find(bulkyItemsTypes, {
            id: settingItem?.bulkyItemTypeId,
          }) as TechnicalType;

          return {
            ...item,
            bulkyCategoryTypeId: item.bulkyCategoryTypeId,
            categoryLabelToDisplay: categoryType?.name,
            itemLabelToDisplay:
              item.customBulkyItemName || itemType?.name || settingItem?.customBulkyItemName || "",
            price: item.price || settingItem?.price || settingCategory?.price,
            name: `items._${settingCategory?.bulkyCategoryTypeId}_${
              item.bulkyItemTypeId || item.id
            }`,
          };
        }),
        (result, item) => {
          return {
            ...result,
            [`_${item?.bulkyCategoryTypeId}_${item.bulkyItemTypeId || item.id}`]: item,
          };
        },
        {}
      ) as any;
      dispatch(change(WIZARD_FORM_NAME, "items", selectedItems));
      dispatch(
        change(
          WIZARD_FORM_NAME,
          "itemsMultiselect",
          map(scheduledItem.items, (item) => (item.bulkyItemTypeId || item.id)?.toString())
        )
      );

      scheduledItem.firstName &&
        dispatch(change(WIZARD_FORM_NAME, "firstName", scheduledItem.firstName));
      scheduledItem.lastName &&
        dispatch(change(WIZARD_FORM_NAME, "lastName", scheduledItem.lastName));
      scheduledItem.phoneNumber &&
        dispatch(change(WIZARD_FORM_NAME, "phoneNumber", scheduledItem.phoneNumber));
    }
  }, [bulkyItemsTypes, categoryTypes, citySettings, dispatch, isSummary, scheduledItem, vendorUrl]);

  const hasValuesInForm =
    isSummary ||
    ((!!thisFormValues?.address ||
      thisFormValues?.email ||
      thisFormValues?.itemsMultiselect?.length ||
      thisFormValues?.items) &&
      hasCompletedOneStep);

  const progressBarValue = step * (100 / WizardSteps.confirmation);

  const handleScheduleAnotherPickup = () => {
    dispatch(reset(WIZARD_FORM_NAME));
    dispatch(push(`/${vendorUrl}/request`));
    setStep(WizardSteps.address);
  };

  const handleSchedulePickup = (data: SchedulerWizardFormValues) => {
    if (isSummary) {
      const payload: ContactInformation = {
        ...pick(data, ["email", "firstName", "lastName", "phoneNumber"]),
        zoneId: addressValidation.zoneIds && addressValidation?.zoneIds[0],
      };

      updateContactInfo(
        confirmationId,
        payload
      )(dispatch)
        .then(() => {
          createSuccessNotification(translate("alertMessages.contactInfoUpdated"))(dispatch);
          setStep(WizardSteps.welcomeBack);
        })
        .catch(() => {
          createErrorNotification(translate("alertMessages.generalError"))(dispatch);
        });
    } else {
      const payload = {
        ...omit(data, ["itemsMultiselect"]),
        zoneId: addressValidation.zoneIds && addressValidation?.zoneIds[0],
        address: {
          ...omit(data.address, ["formattedAddress"]),
          line1: data.address.line1 || data.address.formattedAddress,
        },
        items: map(keys(data.items), (key) => {
          return {
            ...omit(data.items[key], [
              "categoryLabelToDisplay",
              "itemLabelToDisplay",
              "name",
              "unassignedJobId",
              "message",
              "isCancelled",
              "price",
              "cancelDate",
            ]),
            bulkyItemTypeId:
              data.items[key].bulkyCategoryTypeId === BULK_ITEM_CATEGORY_OTHER_ID
                ? null
                : data.items[key].bulkyItemTypeId,
            customBulkyItemName:
              data.items[key].bulkyCategoryTypeId === BULK_ITEM_CATEGORY_OTHER_ID
                ? data.items[key].itemLabelToDisplay || data.items[key].customBulkyItemName
                : null,
          };
        }),
      } as BulkyItemSchedulerPayload;

      saveScheduledItem(payload)(dispatch)
        .then((id) => {
          if (id) dispatch(push(`/${vendorUrl}/summary/${id}`, { isFromConfirmation: true }));
          else createErrorNotification(translate("alertMessages.cancelProcessed"))(dispatch);
        })
        .catch(() => createErrorNotification(translate("alertMessages.generalError"))(dispatch));
    }
  };

  const onCancelUpdateContactInformation = () => {
    setStep(WizardSteps.confirmation);
  };

  return (
    <>
      <Container fluid>
        <Row fullHeight>
          <Column lg={6} padding="no medium" desktopPadding="no xxLarge" verticalScroll>
            <Wrapper padding="0 50px" maxWidth={600} margin="no auto">
              <Separator height={50} />
              {!isSummary && <ProgressBar progress={progressBarValue} />}
              <Separator height={50} />
              {step !== WizardSteps.confirmation && step !== WizardSteps.welcomeBack ? (
                <>
                  <TextDisplay
                    size="xLarge"
                    block
                    weight="medium"
                    margin="large no no"
                    lineHeight="1.2"
                  >
                    {translate("wizardPage.heading", {
                      companyName: vendorFromUrl,
                    })}
                  </TextDisplay>
                  <TextDisplay
                    size="medium"
                    block
                    margin="xSmall no no"
                    desktopMargin="medium no no"
                  >
                    <b>
                      {step === WizardSteps.contactInfo
                        ? translate("wizardPage.updateContactInformationSubheading")
                        : translate("wizardPage.subHeading")}
                    </b>
                  </TextDisplay>
                  <Separator height={30} />
                  {step === WizardSteps.address && (
                    <AddressForm
                      onSubmit={() => {
                        if (!addressValidationError) {
                          setStep(WizardSteps.itemsSelection);
                          !hasCompletedOneStep && setHasCompletedOneStep(true);
                        }
                      }}
                    />
                  )}
                  {step === WizardSteps.itemsSelection && (
                    <ItemsSelectionForm
                      onSubmit={() => {
                        if (!maxItemsPerRequestReached) setStep(WizardSteps.itemsDates);
                      }}
                      goToPreviousStep={() => setStep(WizardSteps.address)}
                    />
                  )}
                  {step === WizardSteps.itemsDates && (
                    <ItemsPickupDateForm
                      onSubmit={() => setStep(WizardSteps.contactInfo)}
                      goToPreviousStep={() => setStep(WizardSteps.itemsSelection)}
                    />
                  )}
                  {step === WizardSteps.contactInfo && (
                    <ContactInfoForm
                      isSummary={isSummary}
                      onSubmit={handleSchedulePickup}
                      onCancelUpdateContactInformation={onCancelUpdateContactInformation}
                      goToPreviousStep={() => setStep(WizardSteps.itemsDates)}
                    />
                  )}
                </>
              ) : (
                <>
                  {step === WizardSteps.confirmation && (
                    <ConfirmationScreen handleScheduleAnotherPickup={handleScheduleAnotherPickup} />
                  )}
                  {step === WizardSteps.welcomeBack && (
                    <WelcomeBackScreen handleScheduleAnotherPickup={handleScheduleAnotherPickup} />
                  )}
                </>
              )}
            </Wrapper>
          </Column>
        </Row>
      </Container>

      <RightDisplayPanel
        backgroundImage={!hasValuesInForm ? BulkyBGImage : undefined}
        bottomLogo={!hasValuesInForm ? RubiconLogo : undefined}
        backgroundColor={!hasValuesInForm ? "transparent" : "grayLight"}
      >
        {!hasValuesInForm ? (
          <>
            <TextDisplay size="extraLarge" color="white" weight="medium">
              {translate("bannerContent.sayGoodbyToBulkyItems")}
            </TextDisplay>
            <TextDisplay
              size="large"
              color="grayDark"
              margin="sMedium no no"
              desktopMargin="medium no no"
            >
              {translate("bannerContent.makeLifeEasier")}
            </TextDisplay>
            <TextDisplay
              size="large"
              color="grayDark"
              margin="sMedium no no"
              desktopMargin="medium no no"
            >
              {translate("bannerContent.ourSolution")}
            </TextDisplay>
          </>
        ) : (
          <SummarySection isSummary={isSummary || false} setStep={setStep} step={step} />
        )}
      </RightDisplayPanel>
    </>
  );
};

export default SchedulerWizardPage;
